import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import type { ReactNode } from "react";

interface RowThumbnailDwellPreviewProps {
  /** The trigger element — a list-row thumbnail tile. */
  children: ReactNode;
  /** URL of the full-size preview image. When absent the wrapper is
   *  a pass-through — a dwell-preview with nothing to show would
   *  just produce an empty floating box. */
  previewUrl?: string;
  /** Accessible label for the preview image (usually the row name). */
  alt: string;
  /** Popover width in pixels. Defaults to 320. */
  width?: number;
  /** Class applied to the `span` that wraps the trigger child.
   *  Defaults to `inline-flex shrink-0` so the small tile stays
   *  compact in list rows. */
  triggerClassName?: string;
}

/**
 * Dwell-triggered image preview for list-row thumbnails. Mirrors the
 * WorkspaceTabBar's hover preview pattern: the small tile along the
 * row keeps its normal interactions, and after a 500 ms dwell a
 * larger rendition of the preview floats beside the row on a
 * portal-mounted tooltip.
 *
 * The 500 ms dwell is deliberately slower than the default tooltip
 * delay — row thumbnails sit inside a draggable, clickable, often
 * keyboard-navigated row, and we don't want a glancing hover during
 * marquee selection or a scroll-hover to pop a preview over the
 * next row the user is trying to click.
 *
 * Pass-through when no `previewUrl` is available; callers should
 * still render the tile as the trigger in both cases so the row
 * stays consistent across files-with-previews and files-without.
 */
export function RowThumbnailDwellPreview({
  children,
  previewUrl,
  alt,
  width = 320,
  triggerClassName = "inline-flex shrink-0",
}: RowThumbnailDwellPreviewProps) {
  if (!previewUrl) return <>{children}</>;
  return (
    <Tooltip delayDuration={500}>
      {/** `asChild` + a native `span` — the span forwards refs
       *   natively and contributes no extra semantics, so the
       *   trigger doesn't accidentally introduce a `<button>` into
       *   a draggable row (which would swallow the row's own
       *   pointerdown/click handlers). Native elements can't be
       *   directly `asChild` targets in Radix 1.x, but wrapping the
       *   caller's thumbnail in a lightweight `span` gives the Slot
       *   something it can safely compose with. */}
      <TooltipTrigger asChild>
        <span className={triggerClassName}>{children}</span>
      </TooltipTrigger>
      <TooltipContent
        side="right"
        align="start"
        sideOffset={12}
        /** `alignOffset` pulls the preview up by the popover's own
         *  padding so the image inside — not the card's outer edge —
         *  sits flush with the trigger row's top. Without the
         *  offset the `p-1` (4px) breathing room around the image
         *  shows as a gap between the row and the preview image. */
        alignOffset={-4}
        hideArrow
        style={{
          width: `${width}px`,
          maxWidth: `min(${width}px, calc(100vw - 1rem))`,
        }}
        className={cn(
          "p-1",
          "border bg-popover text-popover-foreground shadow-xl shadow-black/20",
        )}
      >
        {/** 16:10 window into the source image — the image renders
         *   at full popover width with its natural aspect ratio, and
         *   the fixed-ratio container clips anything past the
         *   bottom. For tall portrait documents (PDFs, screenshots,
         *   article captures) that surfaces the top third or so,
         *   which is reliably where the identifying content lives;
         *   landscape captures still fit entirely. The 16:10 shape
         *   is deliberately a hair taller than standard 16:9
         *   thumbnails so document headers don't get sliced just
         *   below the title. */}
        <div className="w-full aspect-[16/10] overflow-hidden rounded-md border bg-muted">
          <img
            src={previewUrl}
            alt={alt}
            draggable={false}
            className="block w-full h-auto"
          />
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
